import { Link } from 'react-router-dom';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { ArrowRight, Sparkles } from 'lucide-react';

const P = ({ children, className = '' }) => <p className={`mb-3 last:mb-0 leading-relaxed ${className}`}>{children}</p>;

const FEATURED = [
  {
    q: 'What is Sister Diamonds?',
    a: (
      <>
        <P>Sister Diamonds is a sacred sisterhood, coaching community, and transformational experience created for high-capacity Black women who are ready to stop carrying everything alone.</P>
        <P>We create spaces where accomplished women can exhale, reconnect with themselves, receive meaningful support, and move into their next chapter with greater clarity, wholeness, and intention.</P>
        <P>This is not about becoming more impressive. It is about becoming more fully YOU.</P>
      </>
    ),
  },
  {
    q: 'Who is Sister Diamonds for?',
    a: (
      <>
        <P>Sister Diamonds is for high-capacity Black women who are often the leaders, achievers, problem-solvers, caregivers, visionaries, and strong ones in their families, workplaces, businesses, and communities.</P>
        <P>You may be navigating a career transition, leadership challenge, relationship change, empty nest, caregiving season, new calling, business decision, or simply the realization that the life you built no longer feels like the life you want.</P>
        <P>You do not have to be in crisis to belong here. Sometimes the question is simply, &ldquo;What is next for me?&rdquo;</P>
      </>
    ),
  },
  {
    q: 'What do you mean by a "high-capacity woman"?',
    a: (
      <>
        <P>A high-capacity woman is someone who is accustomed to carrying a lot and carrying it well.</P>
        <P>She may lead teams, businesses, families, organizations, communities, or all of the above. Others often depend on her strength, judgment, wisdom, and ability to make things happen.</P>
        <P>But being capable does not mean you were meant to carry everything alone.</P>
        <P>Sister Diamonds gives the woman everyone else relies on a place where she can also be seen, supported, challenged, restored, and poured into.</P>
      </>
    ),
  },
  {
    q: 'Is Sister Diamonds only for Black women?',
    a: (
      <>
        <P>Sister Diamonds was intentionally created to center the lived experiences, brilliance, complexity, and wholeness of Black women.</P>
        <P>Our sisterhood provides a space where Black women do not have to explain or minimize parts of themselves in order to belong. They can bring their ambition, softness, leadership, spirituality, questions, joy, exhaustion, dreams, and whole selves into the room.</P>
      </>
    ),
  },
  {
    q: 'What makes Sister Diamonds different from traditional coaching?',
    a: (
      <>
        <P>We do not separate strategy from the woman who has to live it.</P>
        <P>Traditional coaching can focus heavily on goals, performance, and achievement. Those things matter, but we also ask deeper questions.</P>
        <P>Who are you becoming?</P>
        <P>What are you carrying that no longer belongs to you?</P>
        <P>What do you actually want?</P>
        <P>What would change if your next decision came from wholeness rather than obligation, fear, or expectation?</P>
        <P>Our work combines coaching, reflection, community, accountability, and restorative experiences so that transformation is not just something you understand intellectually. It becomes something you live.</P>
      </>
    ),
  },
  {
    q: 'What is the YUMMY Methodology™?',
    a: (
      <>
        <P>YUMMY stands for Your Ultimate Most Meaningful Yet™.</P>
        <P>There will always be many good things you could do. The more important question is: What is your YUMMY move?</P>
        <P>Your YUMMY move is the choice, shift, conversation, boundary, decision, or next step that is most meaningful for the season you are in now.</P>
        <P>Our methodology helps you reconnect with your truth, receive meaningful support, transform with intention, take purposeful action, and create a legacy aligned with who you are becoming.</P>
      </>
    ),
  },
  {
    q: 'Is Sister Diamonds therapy?',
    a: (
      <>
        <P>No. Sister Diamonds provides coaching, community, personal development, reflection, and transformational experiences. It is not psychotherapy, psychiatric treatment, or a substitute for licensed mental health care.</P>
        <P>We deeply value emotional well-being and encourage every woman to seek the type of professional support that is appropriate for her needs.</P>
      </>
    ),
  },
  {
    q: 'How do I know which Sister Diamonds experience is right for me?',
    a: (
      <>
        <P>Start by asking yourself one question:</P>
        <P className="font-medium">What do I need in this season?</P>
        <P>If you want ongoing sisterhood and nourishment, explore the Inner Circle.</P>
        <P>If you are ready for deeper personal work, explore our coaching and transformational pathways.</P>
        <P>If you are longing for space away from your normal environment to release, restore, reconnect, and gain clarity, explore an upcoming retreat.</P>
        <P>And if you still are not sure, schedule a Soul Connection Call. We will help you understand the options without pressuring you into a program that is not right for you.</P>
      </>
    ),
  },
  {
    q: 'What if I am already successful? Why would I need coaching?',
    a: (
      <>
        <P>Success does not eliminate the need for support.</P>
        <P>In fact, greater responsibility can make it harder to find spaces where you can be completely honest, uncertain, vulnerable, challenged, or simply human.</P>
        <P>Coaching is not an indication that something is wrong with you. It creates dedicated space to examine what you may not be able to see from inside your own life and to make intentional decisions about what comes next.</P>
      </>
    ),
  },
];

const SECTIONS = [
  {
    title: 'About Sister Diamonds',
    items: [
      {
        q: 'Do I have to be an executive or entrepreneur to join?',
        a: (
          <>
            <P>No. Sister Diamonds is about the woman, not her title.</P>
            <P>Some of our sisters lead organizations or businesses. Others are navigating career transitions, retirement, caregiving, relationships, personal reinvention, purpose, or major life decisions.</P>
            <P>What connects us is not a job title. It is the desire to live with greater intention, authenticity, peace, and meaning.</P>
          </>
        ),
      },
    ],
  },
  {
    title: 'The Sister Diamonds Experience',
    items: [
      {
        q: 'What does "Just Be" mean?',
        a: (
          <>
            <P>For many high-achieving women, life has been organized around doing, producing, solving, achieving, helping, and carrying.</P>
            <P>&ldquo;Just Be&rdquo; is permission to experience yourself beyond what you accomplish for everyone else.</P>
            <P>It does not mean abandoning ambition. It means creating enough space to hear yourself again so your actions come from clarity rather than constant striving.</P>
          </>
        ),
      },
      {
        q: 'What do you mean by "soft power"?',
        a: (
          <>
            <P>Soft power is not weakness, passivity, or making yourself smaller.</P>
            <P>It is strength that does not require constant force.</P>
            <P>It is knowing when to move and when to rest. When to speak and when to listen. When to hold something and when to release it. When to say yes and when a sacred no is necessary.</P>
            <P>For many of our sisters, soft power means discovering that they do not have to exhaust themselves to be powerful.</P>
          </>
        ),
      },
      {
        q: 'Is Sister Diamonds a religious program?',
        a: (
          <>
            <P>Our work honors the inner life. You may encounter reflection, mindfulness, spirituality, ritual, purpose, and language about what is sacred.</P>
            <P>We are not here to tell you what to believe. We invite each woman to connect with these experiences in a way that is authentic to her own beliefs, values, and spiritual journey.</P>
          </>
        ),
      },
    ],
  },
  {
    title: 'Membership and Pathways',
    items: [
      {
        q: 'What is the Sister Diamonds Inner Circle?',
        a: (
          <>
            <P>The Inner Circle is our ongoing community for women who want consistent connection, reflection, encouragement, and sisterhood.</P>
            <P>Members receive experiences such as weekly Soul Food, reflection prompts, live gatherings, community connection, access to our content Vault, and priority invitations to select Sister Diamonds experiences.</P>
            <P>Think of it as a place to stay connected to yourself while being surrounded by women committed to doing the same.</P>
          </>
        ),
      },
      {
        q: 'What if I want more support than the Inner Circle provides?',
        a: (
          <>
            <P>We offer several pathways because every woman does not need the same level of support.</P>
            <P>Some women primarily want community and ongoing nourishment. Others are ready for deeper coaching, focused transformation, immersive experiences, or more personalized support.</P>
            <P>Our pathways include the Inner Circle, Inner Edge, Signature Coaching, and the Sister Diamonds Collective.</P>
            <P>If you are unsure where you belong, schedule a Soul Connection Call and we can help you understand the options.</P>
          </>
        ),
      },
      {
        q: 'What is Signature Coaching?',
        a: (
          <>
            <P>Signature Coaching is for women who want more personalized guidance around the specific season, decision, transition, opportunity, or challenge they are navigating.</P>
            <P>Rather than giving you a predetermined formula for your life, our coaches help you identify what matters most, recognize patterns and blind spots, clarify your YUMMY move, and take meaningful action.</P>
          </>
        ),
      },
      {
        q: 'What is the Sister Diamonds Collective?',
        a: (
          <>
            <P>The Sister Diamonds Collective is our deepest level of support for women who want an immersive transformation experience.</P>
            <P>It brings together coaching, experiences, sisterhood, accountability, and expanded access to the Sister Diamonds community and coaching team.</P>
            <P>It is designed for the woman who is ready to make a significant investment in herself and the life she is becoming.</P>
          </>
        ),
      },
      {
        q: 'Who are the Sister Diamonds coaches?',
        a: (
          <>
            <P>Our coaching community includes seasoned and certified coaches with diverse professional backgrounds, specialties, perspectives, and life experiences.</P>
            <P>Crystal Khalil and Dr. Nicole Steele personally guide the Sister Diamonds vision and bring their complementary perspectives to the work. We affectionately call their partnership the Wonder Twin approach, combining strategic insight with deep, soul-centered exploration.</P>
            <P>Depending on your chosen pathway, you may also have access to other carefully selected coaches within our community.</P>
          </>
        ),
      },
    ],
  },
  {
    title: 'Retreats and Experiences',
    items: [
      {
        q: 'What happens at a Sister Diamonds retreat?',
        a: (
          <>
            <P>Our retreats are intentionally designed to be more than beautiful trips.</P>
            <P>Yes, you can expect rest, connection, laughter, and exceptional experiences. But you can also expect space to reflect, release, reconnect, dream, have meaningful conversations, and examine what you want your next chapter to look like.</P>
            <P>You are not coming away simply entertained.</P>
            <P>Our intention is for you to return home more connected to yourself.</P>
          </>
        ),
      },
      {
        q: 'Do I need to come to a retreat knowing exactly what I want to work on?',
        a: (
          <>
            <P>Absolutely not.</P>
            <P>Sometimes clarity is the result of creating enough space to finally hear yourself.</P>
            <P>You may arrive with a specific question or goal. You may simply know that something needs to change. Both are welcome.</P>
          </>
        ),
      },
      {
        q: 'Can I attend a retreat by myself?',
        a: (
          <>
            <P>Yes.</P>
            <P>You do not need to bring a friend or already know someone in the Sister Diamonds community.</P>
            <P>One of the gifts of these experiences is discovering what happens when women who may arrive as strangers create authentic connection with one another.</P>
          </>
        ),
      },
      {
        q: 'Are Sister Diamonds retreats vacations?',
        a: (
          <>
            <P>They are intentionally restorative and often held in beautiful environments, but they are not traditional vacations.</P>
            <P>Our retreats combine spaciousness and enjoyment with facilitated experiences designed to support reflection, connection, transformation, and intentional next steps.</P>
            <P>You will have room to breathe, but there is also purpose behind the experience.</P>
          </>
        ),
      },
    ],
  },
  {
    title: 'Joining the Sisterhood',
    items: [
      {
        q: 'What if I am not sure I am ready?',
        a: (
          <>
            <P>You do not have to have everything figured out.</P>
            <P>In fact, &ldquo;I know something needs to shift, but I don&apos;t know exactly what&rdquo; is often a powerful place to begin.</P>
            <P>Explore the community, learn about our pathways, or schedule a Soul Connection Call. You can decide what feels aligned for this season of your life.</P>
          </>
        ),
      },
      {
        q: 'What if I am tired of always working on myself?',
        a: (
          <>
            <P>Then Sister Diamonds may feel refreshingly different.</P>
            <P>We do not believe your life should become one endless self-improvement project.</P>
            <P>Sometimes transformation looks like doing something.</P>
            <P>Sometimes it looks like releasing something.</P>
            <P>And sometimes the most powerful thing you can do is rest long enough to remember who you are.</P>
          </>
        ),
      },
      {
        q: 'Will someone tell me what I should do with my life?',
        a: (
          <>
            <P>No.</P>
            <P>Our role is not to hand you someone else&apos;s definition of success.</P>
            <P>We ask questions, offer perspectives, identify patterns, challenge assumptions, create accountability, and help you hear your own wisdom more clearly.</P>
            <P>The goal is not dependence on a coach. It is greater trust in yourself.</P>
          </>
        ),
      },
      {
        q: 'What if I am usually the person supporting everyone else?',
        a: (
          <>
            <P>Then you are exactly the kind of woman we had in mind when we created Sister Diamonds.</P>
            <P>You deserve spaces where you are not responsible for managing the room, solving the problem, taking care of everyone else, or having all the answers.</P>
            <P>Here, the woman who usually pours into everyone else gets poured into too.</P>
          </>
        ),
      },
      {
        q: 'How much does Sister Diamonds cost?',
        a: (
          <>
            <P>Investment varies by experience and level of support.</P>
            <P>Current pricing is displayed with each Sister Diamonds pathway or provided during the enrollment process. Because retreats, special experiences, and coaching engagements can differ, always refer to the specific program page for current investment information.</P>
          </>
        ),
      },
      {
        q: 'Are payment plans available?',
        a: (
          <P>Payment options vary by program. When payment plans are available, the specific terms will be provided during registration or enrollment.</P>
        ),
      },
      {
        q: 'What are your cancellation and refund policies?',
        a: (
          <>
            <P>Cancellation, transfer, and refund terms may differ depending on the membership, coaching program, retreat, or special experience you select.</P>
            <P>Please review the terms provided at the time of enrollment before completing your purchase. If you have questions about a specific program, contact our team before registering.</P>
          </>
        ),
      },
      {
        q: 'Is what I share within Sister Diamonds confidential?',
        a: (
          <>
            <P>We believe meaningful sisterhood requires trust.</P>
            <P>Members are expected to treat the personal stories and experiences shared by other women with care and discretion. Specific confidentiality expectations for coaching, group programs, retreats, and community spaces are explained as part of those experiences.</P>
          </>
        ),
      },
      {
        q: 'How do I get started?',
        a: (
          <>
            <P>You do not need to know your entire path.</P>
            <P>You only need to identify your next meaningful step.</P>
            <P>Explore our Sister Diamonds pathways, join the Inner Circle, register for an upcoming experience, or schedule a Soul Connection Call.</P>
            <P>Your next chapter does not have to begin with another achievement.</P>
            <P>It can begin with a decision to choose yourself.</P>
            <P className="font-heading font-semibold text-forest">What is your YUMMY move?™</P>
          </>
        ),
      },
    ],
  },
];

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

export default function FAQPage() {
  return (
    <div className="min-h-screen bg-cream">
      <Navbar />

      {/* Hero */}
      <section className="bg-forest pt-32 pb-16 px-4 sm:px-6 lg:px-8 text-center">
        <div className="max-w-3xl mx-auto">
          <Badge className="bg-terracotta/20 text-terracotta mb-4">Frequently Asked Questions</Badge>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
            Everything You&apos;re Wondering, Sis
          </h1>
          <p className="text-white/85 text-lg leading-relaxed">
            We are not here to make you more impressive.<br className="hidden sm:block" />
            We are here to help you become <span className="text-terracotta font-medium">more fully you</span>.
          </p>
        </div>
      </section>

      {/* Featured questions */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-2 mb-6">
            <Sparkles className="h-5 w-5 text-terracotta" />
            <h2 className="font-heading text-lg font-semibold text-forest uppercase tracking-wide">Start Here</h2>
          </div>
          <Accordion type="single" collapsible className="bg-white rounded-2xl shadow-sm border border-sand px-6" data-testid="faq-featured">
            {FEATURED.map((item) => (
              <AccordionItem key={item.q} value={slug(item.q)} data-testid={`faq-item-${slug(item.q)}`}>
                <AccordionTrigger className="font-heading text-left text-lg text-forest hover:text-terracotta hover:no-underline py-5">
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 text-base pb-6">{item.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Categorized sections */}
      <section className="pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto space-y-12">
          {SECTIONS.map((section) => (
            <div key={section.title} data-testid={`faq-section-${slug(section.title)}`}>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-forest mb-4">{section.title}</h2>
              <Accordion type="single" collapsible className="bg-white rounded-2xl shadow-sm border border-sand px-6">
                {section.items.map((item) => (
                  <AccordionItem key={item.q} value={slug(item.q)} data-testid={`faq-item-${slug(item.q)}`}>
                    <AccordionTrigger className="font-heading text-left text-lg text-forest hover:text-terracotta hover:no-underline py-5">
                      {item.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-gray-600 text-base pb-6">{item.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-forest text-center">
        <div className="max-w-2xl mx-auto">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">Still have questions?</h2>
          <p className="text-white/85 text-lg mb-8">
            Schedule a Soul Connection Call and we&apos;ll help you find the pathway that fits this season of your life — no pressure, just clarity.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="https://calendly.com/d/crgj-6gb-fcg/discovery-call?month=2026-09" target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="bg-terracotta hover:bg-terracotta/90 text-white px-8" data-testid="faq-connection-call-btn">
                Book a Soul Connection Call <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </a>
            <Link to="/pathways">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 px-8" data-testid="faq-pathways-btn">
                Explore Pathways
              </Button>
            </Link>
          </div>
          <p className="text-white/60 text-sm mt-10 italic">What is your YUMMY move?™</p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
